import React, { FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import { HeaderRight } from '../../../layout/Header/Header';
import ThemeContext from '../../../contexts/themeContext';
import Popovers from '../../../components/bootstrap/Popovers';
import NotificationDrawer from '../../../components/notifications/NotificationDrawer';
import notificationService from '../../../services/notificationService';
import authService from '../../../services/authService';

interface ICommonHeaderRightProps {
	beforeChildren?: ReactNode;
	afterChildren?: ReactNode;
}

const CommonHeaderRight: FC<ICommonHeaderRightProps> = ({ beforeChildren, afterChildren }) => {
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const styledBtn: IButtonProps = {
		color: 'light',
		hoverShadow: 'default',
		isLight: true,
		size: 'lg',
	};

	const [offcanvasStatus, setOffcanvasStatus] = useState(false);
	const [unreadCount, setUnreadCount] = useState<number>(0);
	const hasFetchedOnMountRef = useRef<boolean>(false);

	const refreshUnread = useCallback(async (force = false) => {
		if (authService.isAuthenticated()) {
			const count = await notificationService.getUnreadCount(force);
			setUnreadCount(count);
		}
	}, []);

	useEffect(() => {
		if (!hasFetchedOnMountRef.current) {
			hasFetchedOnMountRef.current = true;
			refreshUnread(false);
		}

		const handleNewNotification = () => {
			refreshUnread(true);
		};

		window.addEventListener('stichmaster:new_notification', handleNewNotification);
		return () => {
			window.removeEventListener('stichmaster:new_notification', handleNewNotification);
		};
	}, [refreshUnread]);

	return (
		<HeaderRight>
			<div className='row g-3'>
				{beforeChildren}

				{/* Full Screen */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Fullscreen'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
							onClick={() => setFullScreenStatus(!fullScreenStatus)}
							aria-label='Toggle fullscreen'
						/>
					</Popovers>
				</div>

				{/* Notifications */}
				<div className='col-auto'>
					<div className='position-relative d-inline-flex align-items-center'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon='Notifications'
							onClick={() => setOffcanvasStatus(true)}
							aria-label='Notifications'
						/>
						{unreadCount > 0 && (
							<span
								className='position-absolute'
								style={{
									top: '-2px',
									right: '-2px',
									transform: 'translate(25%, -25%)',
									backgroundColor: '#f95738',
									color: '#ffffff',
									fontSize: '0.65rem',
									fontWeight: 700,
									minWidth: '18px',
									height: '18px',
									lineHeight: '18px',
									padding: '0 4px',
									borderRadius: '999px',
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									boxShadow: '0 0 0 2px #ffffff',
									pointerEvents: 'none',
									zIndex: 5,
								}}>
								{unreadCount > 99 ? '99+' : unreadCount}
							</span>
						)}
					</div>
				</div>
				{afterChildren}
			</div>

			<NotificationDrawer
				isOpen={offcanvasStatus}
				setOpen={setOffcanvasStatus}
				unreadCount={unreadCount}
				setUnreadCount={setUnreadCount}
			/>
		</HeaderRight>
	);
};

CommonHeaderRight.propTypes = {
	beforeChildren: PropTypes.node,
	afterChildren: PropTypes.node,
};

CommonHeaderRight.defaultProps = {
	beforeChildren: null,
	afterChildren: null,
};

export default CommonHeaderRight;
